/* eslint-disable no-param-reassign */
import { Frame, Keys, Point, Rect, Touches, View } from './view';
import { CanvasOpAndroid } from '@fullstory/mobile-flatbuffers';

export const SCROLL_NOT_WELL_BEHAVED = [Infinity];

// TODO(mmastrac): Why doesn't this exist in tsc?
function sign(x: number) {
  const m: any = Math;
  return m.sign(x);
}

// iOS view coordinates start as floats, but are truncated during capture
// view size and motion are computed by taking differences of these integer values,
// so the same underlying property can result in different final values
//
// android views have integer coordinates internally, so this logic isn't needed
// for exact matches. We use this function as a loose heuristic, so it's probably
// not too risky
function viewValuesAreCloseEnough(a: number, b: number): boolean {
  // each value in the difference that computed a or b
  // could have been rounded up or down, so in the worst case
  // we could see x+1 and x-1, a difference of 2
  return Math.abs(a - b) <= 2;
}

// we are conservative with choosing to interpolate. We only interpolate position,
// so playback of a resizing view may be misleading compared to just showing frame
// by frame
function sizesAllowInterpolation(from: Rect, to: Rect, xScroll: number): boolean {
  if (!viewValuesAreCloseEnough(from.width(), to.width())) {
    return false;
  }
  // special case views that look like they are part of infinite scrolling
  // the idea is such a view may add or remove content at the top or bottom, thus changing size,
  // without those changes actually appearing on screen. *Not* interpolating such views
  // would remove the impression of scrolling
  // the size cutoff is arbitrary, probably a much smaller value would be reasonable
  if (xScroll === 0 && from.height() > 10000 && to.height() > 10000) {
    return true;
  }
  return viewValuesAreCloseEnough(from.height(), to.height());
}

export class Interpolation {
  topView: InterpolatedView;
  touches: Touches[];
  keys: Keys;
  f1: Frame;
  f2: Frame;
  fraction = 0; // How far along the interpolation are we? Should be a number from 0 to 1 (inclusive)

  constructor(topView: InterpolatedView, touches: Touches[], keys: Keys, f1: Frame, f2: Frame) {
    this.topView = topView;
    this.touches = touches;
    this.keys = keys;
    this.f1 = f1;
    this.f2 = f2;
  }

  public setTime(currentTime: number) {
    // The currentTime is the exact millisecond of the replay that we're trying to render right now.
    // That's almost always somewhere in between two different recorded frames (f1 & f2), so we need
    // to calculate this.fraction to determine how the two frames are blended together.
    //
    // When this.fraction is 0, we're just rendering the contents of f1, when it's 1, we're just
    // rendering the contents of f2. When it's 0.5, we render something halfway between the two, etc.
    //
    // Additionally, we need to take into account skipped frames. The fraction should remain at 0
    // during the skipped frames between f1 and f2, because they are effectively duplicates of f1.
    //
    // Finally, note that when playback first starts, currentTime will be *before* f1. So the fraction
    // just gets clamped to 0 in that case.

    // all timestamps are offsets in ms since the begging of the current "page" (app foreground-to-background)
    const startTime = this.f1.timestamp;
    const endTime = this.f2.timestamp;

    // the total number of milliseconds that this interpolation covers
    const range = endTime - startTime;

    // the number of skipped frames included in this interpolation (skipped frames should all be rendered as f1)
    const skipped = this.f2.idleFramesSkipped + this.f2.identicalFramesSkipped;

    // the timestamp when we're done with skipped frames and should begin interpolating towards f2
    // (animation begins immediately when there are no skipped frames)
    let animationStart = startTime + (range / (skipped + 1)) * skipped;

    // the number of milliseconds that are animated in this interpolation
    let animationRange = endTime - animationStart;

    // to prevent smearing, limit the animation range to the last 500ms (most frames are 250ms)
    if (animationRange > 500) {
      animationStart = endTime - 500;
      animationRange = 500;
    }

    // how many MS of the animation have already passed, clamped to the range of 0-animationRange
    const animationElapsed = Math.max(0, Math.min(animationRange, currentTime - animationStart));

    // Finally, turn it into a fraction that can be used when calculating other interpolated values
    this.fraction = animationElapsed / animationRange;
  }

  public linear(startValue: number, endValue: number): number {
    return startValue + (endValue - startValue) * this.fraction;
  }

  // the window size changes on Android when a keyboard is shown or hidden
  public getWindow(): Rect {
    const w1 = this.f1.window;
    const w2 = this.f2.window;
    return new Rect(
      this.linear(w1.x1, w2.x1),
      this.linear(w1.y1, w2.y1),
      this.linear(w1.x2, w2.x2),
      this.linear(w1.y2, w2.y2),
    );
  }
}

interface TranslateInterpolation {
  index: number;
  from: Point;
  to: Point;
  insert?: boolean;
}

export class InterpolationData {
  position: { from: number[]; to: number[] };
  translate: TranslateInterpolation;
}

export class InterpolatedView {
  view: View;
  interpolation: InterpolationData;
  children: InterpolatedView[];

  constructor(view: View, interpolation: InterpolationData, children: InterpolatedView[]) {
    this.view = view;
    this.interpolation = interpolation;
    this.children = children;
  }
}

export class ChildMotion {
  appeared: number = 0;
  disappeared: number = 0;
  textChanged: number = 0;
  resize: boolean = false;
  motionless: number = 0;
  motions: { x: number[]; y: number[]; resize: boolean[] } = { x: [], y: [], resize: [] };
}

/**
 * Interpolates between two frames.
 */
export class Interpolator {
  private f1: Frame;
  private f2: Frame;

  static interpolate(f1: Frame, f2: Frame): Interpolation {
    return new Interpolator().interpolate_(f1, f2);
  }

  /**
   * Interpolates from one frame to another.
   */
  private interpolate_(f1: Frame, f2: Frame): Interpolation {
    const touches = f2.touches;
    const keys = f2.keys;

    this.f1 = f1;
    this.f2 = f2;
    const topView = this.interpolateView(f1.topView, f2.topView);
    return new Interpolation(topView, touches, keys, f1, f2);
  }

  private computeChildMotion(fromView: View, toView: View): ChildMotion {
    // Detect scrolling by looking for correlated motion in child views
    const childMotion = new ChildMotion();

    // If view is the same, just return empty motion data
    if (fromView == toView) return childMotion;

    for (let i = 0; i < toView.children.length; i++) {
      const toChild = toView.children[i];
      const fromChild = this.f1.findViewById(toChild.uniqueId);

      if (fromChild == null) {
        childMotion.appeared++;
      }
    }

    /* Take a pass first to see if we should eliminate a scroll knob.  On
     * iOS, ScrollViews have a child with a content pane and a second
     * (narrow) knob.  So we detect wide views, and if there are any wide
     * views (>50px), then we assume that narrow views (<10px) are too
     * narrow to possibly be legitimate children (they move the opposite
     * direction, after all).
     */
    let foundWideChild = false;
    for (let i = 0; i < fromView.children.length; i++) {
      const child = fromView.children[i];
      if (child.position.x2 - child.position.x1 >= 50) {
        foundWideChild = true;
      }
    }

    for (let i = 0; i < fromView.children.length; i++) {
      const child = fromView.children[i];
      const toChild = this.f2.findViewById(child.uniqueId);
      const isNarrow = child.position.x2 - child.position.x1 < 10;

      // Doesn't exist, so can't contribute to heuristics
      if (toChild == null) {
        childMotion.disappeared++;
        continue;
      }

      /* Too small, with a wider child present? */
      if (foundWideChild && isNarrow) {
        continue;
      }

      const dx = toChild.position.x1 - child.position.x1;
      const dy = toChild.position.y1 - child.position.y1;

      if (dx != 0 || dy != 0) {
        childMotion.motions.x.push(dx);
        childMotion.motions.y.push(dy);
        const resized = !sizesAllowInterpolation(child.position, toChild.position, dx);
        childMotion.resize = childMotion.resize || resized;
        childMotion.motions.resize.push(resized);
      } else {
        childMotion.motionless++;
      }

      if ((child.textContent || toChild.textContent) && child.textContent != toChild.textContent) {
        childMotion.textChanged++;
      }
    }

    return childMotion;
  }

  /**
   * Returns an InterpolatedView.
   */
  private interpolateView(fromView: View, toView: View) {
    const interpolatedChildren = [];
    const scroll = this.computeScroll(fromView, toView);

    if (scroll == SCROLL_NOT_WELL_BEHAVED) {
      // Moving too fast to interpolate
      for (let i = 0; i < fromView.children.length; i++) {
        const child = fromView.children[i];
        interpolatedChildren.push(this.interpolateView(child, child));
      }
    } else if (scroll && (scroll[0] || scroll[1])) {
      // Well-behaved scrolling (one of the two scroll vectors is non-zero)

      // Since we're scrolling, every one of the children must have the same motion vector. If a
      // child appears to have a radically different vector, or appears or disappears, we need to
      // invent a placeholder child that will track scrolling properly.

      /* Note the same logic in computeChildMotion. */
      let foundWideChild = false;
      for (let i = 0; i < fromView.children.length; i++) {
        const child = fromView.children[i];
        if (child.position.x2 - child.position.x1 >= 50) {
          foundWideChild = true;
        }
      }

      // TODO(mmastrac): children in "to" but not in "from" should be tracked as well
      for (let i = 0; i < fromView.children.length; i++) {
        const child = fromView.children[i];
        const toChild = this.f2.findViewById(child.uniqueId);
        const isNarrow = child.position.x2 - child.position.x1 < 10;

        if (toChild && !sizesAllowInterpolation(child.position, toChild.position, scroll[0])) {
          // Resized children are not currently interpolated
          const c1 = this.interpolateView(child, child);
          delete c1.interpolation.position;
          interpolatedChildren.push(c1);
        } else if (!toChild) {
          // Child disappeared, so invent some motion for it
          const c1 = this.interpolateView(child, child);
          c1.interpolation.position = {
            from: [child.position.x1, child.position.y1],
            to: [child.position.x1 + scroll[0], child.position.y1 + scroll[1]],
          };
          interpolatedChildren.push(c1);
        } else {
          const sx = sign(toChild.position.x1 - child.position.x1);
          const sy = sign(toChild.position.y1 - child.position.y1);

          if (sx == 0 && sy == 0) {
            // If this child has no motion, we don't want to move it
            // By default it should have no position interpolation
            interpolatedChildren.push(this.interpolateView(child, child));
          } else if (foundWideChild && isNarrow) {
            // Scroll the scrollie knob independently.
            const c1 = this.interpolateView(child, child);
            c1.interpolation.position = {
              from: [child.position.x1, child.position.y1],
              to: [toChild.position.x1, toChild.position.y1],
            };
            interpolatedChildren.push(c1);
          } else if ((sx && sx != sign(scroll[0])) || (sy && sy != sign(scroll[1]))) {
            // Child is moving in the opposite direction, so let's split it into two and scroll
            // them independently

            const c1 = this.interpolateView(child, child);
            c1.interpolation.position = {
              from: [child.position.x1, child.position.y1],
              to: [child.position.x1 + scroll[0], child.position.y1 + scroll[1]],
            };
            interpolatedChildren.push(c1);

            const c2 = this.interpolateView(toChild, toChild);
            c2.interpolation.position = {
              from: [toChild.position.x1 - scroll[0], toChild.position.y1 - scroll[1]],
              to: [toChild.position.x1, toChild.position.y1],
            };
            interpolatedChildren.push(c2);
          } else {
            const interpolatedChild = this.interpolateView(child, toChild);

            // Force scroll vector
            interpolatedChild.interpolation.position = {
              from: [child.position.x1, child.position.y1],
              to: [child.position.x1 + scroll[0], child.position.y1 + scroll[1]],
            };

            interpolatedChildren.push(interpolatedChild);
          }
        }
      }
    } else {
      const foundChildren = {};

      // No correlated scrolling detected so we'll let the linear interpolator work as designed
      for (let i = 0; i < fromView.children.length; i++) {
        const child = fromView.children[i];
        foundChildren[child.uniqueId] = true;
        let toChild = this.f2.findViewById(child.uniqueId);

        if (!toChild) {
          toChild = child;
        }

        interpolatedChildren.push(this.interpolateView(child, toChild));
      }

      for (let i = 0; i < toView.children.length; i++) {
        const child = toView.children[i];
        if (!foundChildren[child.uniqueId]) {
          // Child appeared
          // TODO(mmastrac): We need to add a canvas op to render this new child
          // console.log('appeared: ', child);

          interpolatedChildren.push(this.interpolateView(child, child));
        }
      }
    }

    const interpolations = new InterpolationData();
    if (fromView.position.x1 != toView.position.x1 || fromView.position.y1 != toView.position.y1) {
      // Assuming the view hasn't changed height, we can interpolate it
      const canInterpolate = sizesAllowInterpolation(
        fromView.position,
        toView.position,
        toView.position.x1 - fromView.position.x1,
      );
      if (canInterpolate) {
        interpolations.position = {
          from: [fromView.position.x1, fromView.position.y1],
          to: [toView.position.x1, toView.position.y1],
        };
      }
    }

    // Does this view have a translate we can tween between states?
    const translate = this.computeTranslate(fromView, toView);
    if (translate) {
      interpolations.translate = translate;
    }

    return new InterpolatedView(fromView, interpolations, interpolatedChildren);
  }

  // XXX: the result of computeScroll has many implications for whether interpolation occurs.
  // In particular, returning zero might be safer than SCROLL_NOT_WELL_BEHAVED, since it leads
  // to trusting the captured child motion, rather than forgoing interpolation
  private computeScroll(fromView: View, toView: View): number[] {
    // If cropping is involved and has changed between frames, we are not well behaved
    if (fromView.hasCrop || toView.hasCrop) {
      if (fromView.hasCrop != toView.hasCrop) return SCROLL_NOT_WELL_BEHAVED;
    }

    const childMotion = this.computeChildMotion(fromView, toView);
    // No motion, no scroll
    if (childMotion.motions.x.length == 0) return [0, 0];
    // Scroll vector is somewhat meaningless, other than its sign
    const scrollVector = [
      sign(toView.scrollF.x - fromView.scrollF.x),
      sign(toView.scrollF.y - fromView.scrollF.y),
    ];
    const scroll = Interpolator.detectScroll(childMotion, scrollVector);

    return scroll;
  }

  public static allMotionsSame(motions: { x: number[]; y: number[] }) {
    for (let i = 1; i < motions.x.length; i++) {
      if (
        !viewValuesAreCloseEnough(motions.x[i], motions.x[0]) ||
        !viewValuesAreCloseEnough(motions.y[i], motions.y[0])
      ) {
        return false;
      }
    }
    return true;
  }

  public static detectScroll(childMotion: ChildMotion, scrollVector: number[]): number[] {
    const motions = childMotion.motions;
    // we're trying to choose a majority vector among the nonzero motions,
    // but this doesn't really make sense if many of the children stayed still.
    // The below logic doesn't work well when scrollVector is zero, so decide now if
    // we have enough signal to decide on zero scroll.

    // conservative ratio by which motionless children must outnumber moving ones
    // before we decide there is scrolling, 1 would be a simple majority
    const zeroMotionRatio = 3;
    if (
      childMotion.motionless > motions.x.length * zeroMotionRatio &&
      scrollVector[0] == 0 &&
      scrollVector[1] == 0
    ) {
      return [0, 0];
    }

    // If we don't have enough children to compare against *and* text is changing, it's probably not well-behaved
    if (motions.x.length <= 2 && childMotion.textChanged > 0) {
      return SCROLL_NOT_WELL_BEHAVED;
    }

    // All motion is in the same direction, so assume that it is right
    if (Interpolator.allMotionsSame(motions)) return [motions.x[0], motions.y[0]];

    // If we have more than one, let's look for "well-behavedness", which means
    // that if you are going in the direction of the scroll vector, you have the same
    // vector as everyone else who is going in that direction

    // XXX: If scrollVector is zero, currently the case on iOS, the behavior may be surprising:
    // childMotions contains only nonzero motion, so no "cohort" will be found, leading to
    // SCROLL_NOT_WELL_BEHAVED if the nonzero child motions differ
    let scrollMotion = null;
    let cohorts = 0;
    for (let i = 0; i < motions.x.length; i++) {
      if (sign(motions.x[i]) == scrollVector[0] && sign(motions.y[i]) == scrollVector[1]) {
        if (scrollMotion == null) {
          scrollMotion = [motions.x[i], motions.y[i]];
        } else {
          if (
            motions.x[i] != scrollMotion[0] ||
            motions.y[i] != scrollMotion[1] ||
            motions.resize[i]
          ) {
            // Not well-behaved
            return SCROLL_NOT_WELL_BEHAVED;
          }

          cohorts++;
        }
      }
    }

    // Not enough correlated motion
    if (cohorts == 0) {
      return SCROLL_NOT_WELL_BEHAVED;
    }

    // More than one, so let's order by vector size and choose the smallest non-zero one
    let smallestSize = +Infinity;
    let smallest = [0, 0];

    for (let i = 0; i < motions.x.length; i++) {
      if (motions.x[i] == 0 && motions.y[i] == 0) continue;

      // if (sign(motions.x[i]) != scrollVector[0] || sign(motions.y[i]) != scrollVector[1])
      // continue;

      const size = Math.pow(motions.x[i], 2) + Math.pow(motions.y[i], 2);
      if (size < smallestSize) {
        smallestSize = size;
        smallest = [motions.x[i], motions.y[i]];
      }
    }

    return smallest;
  }

  /**
   * If two canvas lists differ only in a translate operation we can tween it. Note that we omit empty translate
   * operations at recording time which makes this interesting.
   */
  private computeTranslate(fromView: View, toView: View): TranslateInterpolation {
    // Ensure that we have a canvas for before/after
    if (!fromView.canvas || !toView.canvas) {
      return null;
    }

    // No scroll change
    if (fromView.scroll.x == toView.scroll.x && fromView.scroll.y == toView.scroll.y) {
      return null;
    }

    if (fromView.canvas.length - 1 == toView.canvas.length) {
      return this.computeTranslateOffByOne(toView, fromView, false);
    }

    if (fromView.canvas.length == toView.canvas.length - 1) {
      return this.computeTranslateOffByOne(fromView, toView, true);
    }

    // We support off-by-one or equal, that's it
    if (fromView.canvas.length != toView.canvas.length) {
      return null;
    }

    // Make sure we have the same ops in both
    const translates: number[] = [];
    for (let i = 0; i < fromView.canvas.length; i++) {
      if (fromView.canvas[i].op != toView.canvas[i].op) {
        return null;
      }

      if (fromView.canvas[i].op == CanvasOpAndroid.TRANSLATE) {
        translates.push(i);
      }
    }

    const dx = toView.scroll.x - fromView.scroll.x;
    const dy = toView.scroll.y - fromView.scroll.y;

    // If we find a translate op that changes by the same amount that scroll changed, we have the op we need
    for (let i = 0; i < translates.length; i++) {
      const index = translates[i];
      if (
        fromView.canvas[index].point.x - toView.canvas[index].point.x == dx &&
        fromView.canvas[index].point.y - toView.canvas[index].point.y == dy
      ) {
        return { index, from: fromView.canvas[index].point, to: toView.canvas[index].point };
      }
    }

    return null;
  }

  /**
   * In the case where a translation is off-by-one, we look for the first op in "longer" that doesn't have a matching
   * op in "shorter" and check whether it matches the scroll amount if we were to assume a null translation.
   */
  private computeTranslateOffByOne(
    shorter: View,
    longer: View,
    fromShorter: boolean,
  ): TranslateInterpolation {
    for (let i = 0; i < shorter.canvas.length; i++) {
      // Did we find our missing op?
      if (longer.canvas[i].op != shorter.canvas[i].op) {
        // Not a valid op we can handle
        if (longer.canvas[i].op != CanvasOpAndroid.TRANSLATE) {
          return null;
        }
        const dx = shorter.scroll.x - longer.scroll.x;
        const dy = shorter.scroll.y - longer.scroll.y;
        if (longer.canvas[i].point.x == dx && longer.canvas[i].point.y == dy) {
          if (fromShorter) {
            return { index: i, from: Point.ZERO(), to: longer.canvas[i].point, insert: true }; // requires insertion
          } else {
            return { index: i, from: longer.canvas[i].point, to: Point.ZERO() };
          }
        }
      }
    }
    return null;
  }
}
